/**
 * hooks.ts — data hooks. `useWorkspace` reads the folded workspace and refetches
 * whenever a command writes (via the `onDataChanged` bus). `useRegistry` loads the
 * static registries (primitives, packs, pipeline, provider info) once.
 */
import { useEffect, useState } from "react";
import { getRegistry, getState, getEvents, onDataChanged, type RegistryPayload } from "./api.ts";
import type { Workspace, DomainEvent } from "../../store/events.ts";

export function useWorkspace(): { ws: Workspace | null; reload: () => void } {
  const [ws, setWs] = useState<Workspace | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    getState()
      .then((w) => live && setWs(w))
      .catch(() => live && setWs(null));
    return () => {
      live = false;
    };
  }, [tick]);
  useEffect(() => onDataChanged(() => setTick((t) => t + 1)), []);
  return { ws, reload: () => setTick((t) => t + 1) };
}

export function useEventLog(): readonly DomainEvent[] {
  const [events, setEvents] = useState<readonly DomainEvent[]>([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    getEvents()
      .then((e) => live && setEvents(e))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [tick]);
  useEffect(() => onDataChanged(() => setTick((t) => t + 1)), []);
  return events;
}

let registryCache: RegistryPayload | null = null;
export function useRegistry(): RegistryPayload | null {
  const [reg, setReg] = useState<RegistryPayload | null>(registryCache);
  useEffect(() => {
    if (registryCache) return;
    let live = true;
    getRegistry()
      .then((r) => {
        registryCache = r;
        if (live) setReg(r);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return reg;
}
